'use client';

import { BadgeInfo } from 'lucide-react';
import * as React from 'react';

import { cn } from './cn';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './tooltip';

/**
 * The one field layout — Phase 2 (MR-1) of the MUI → Tailwind migration.
 *
 * WHY THE LABEL IS IN NORMAL FLOW: MUI's label is absolutely positioned above
 * the control (`translate-y-[-100%]` in src/tailwind.css), so it reserves zero
 * layout space and every stacked field historically carried a per-call-site
 * margin spacer — the compensation family this migration deletes. Here the
 * label is a real block: the space above a field is exactly the parent's gap
 * at every display size and scheme, so there is nothing per-field to forget.
 *
 * The layout is DESIGN-SPEC §2.2, verbatim: label → `gap-2` (8px @ medium) →
 * control → `gap-2` → message; between fields the CONTAINER owns the rhythm
 * (`gap-4` in forms). Fields never carry margins, and the compensation family is
 * frozen by a census gate that failed when it grew (removed with the migration; the form-rhythm lint block remains the enforced policy).
 *
 * The label and the message carry `data-slot="field-label"` /
 * `data-slot="field-message"` so the (since-removed) ui-consistency probe could measure
 * the rhythm between those three boxes on a real render rather than inferring it
 * from the class string. That assertion — label → control → message at the
 * declared gap, with no margin on the control — is the property the operator
 * asked for: a form cannot inherit an uneven one from a call site.
 *
 * Sizing: the CONTROL owns its height (the wrapper's `h-9`, or a mixed-row
 * height applied through the control slot). Never constrain the Field root —
 * a height there makes the flex column squash the control instead of the
 * page growing (design-qa measured an 18px click target when a call site put
 * `h-10` on the root); the controls also carry `shrink-0` so a rogue
 * constraint fails loudly rather than silently.
 *
 * Compatibility: every field root carries `minerva-field`, and
 * `src/tailwind.css` scopes the legacy floating-label compensations
 * (`.MuiInputLabel-root` absolutize, `.MuiFormHelperText-root` offset,
 * `MuiFormControl` margin) away from it — so the inert `Mui*` selector hooks
 * the field atoms emit stay visually inert while un-migrated fields keep
 * their compensations. The `Mui*` class names on the label and
 * message below are part of that hook contract; do not rename them.
 *
 * Accessibility: the Field mints a `useId` when no control id is supplied and
 * wires `htmlFor`/`aria-describedby`/`aria-invalid` through context, so any
 * control composed inside (Input, Textarea, future Select) is labelled and
 * described without per-call-site plumbing. Grouped controls (radio/checkbox
 * groups) pass `labelId` and point their group's `aria-labelledby` at it.
 */

/** Mirror of the config `FormField` help shape (kept local: `ui/**` must not
 * import from `features/**`, and this is the canonical copy now). */
export type FieldHelpContent = {
  controls: string;
  increaseEffect?: string;
  decreaseEffect?: string;
  supportedValues?: string;
  options?: string;
  note?: string;
};

export interface FieldControlContextValue {
  /** The id the label points at; controls default their `id` to it. */
  id?: string;
  /** Message element id, present only while a message renders. */
  describedBy?: string;
  invalid?: boolean;
  required?: boolean;
  disabled?: boolean;
}

/**
 * Exported (not just the hook) so a GROUP control can re-provide it. A Field
 * hands its one `id` / `describedBy` / `invalid` to every control inside it,
 * which is right for one input and wrong for a radio/checkbox group: N options
 * would share one DOM id, each repeat the same `aria-describedby`, and each
 * paint its own invalid ring. `ui/choice-group` re-provides `{}` to its
 * options and puts those attributes on the group element instead.
 */
export const FieldControlContext =
  React.createContext<FieldControlContextValue>({});

/**
 * Consumed by controls composed inside a `Field` (Input, Textarea, …) so the
 * label association and the aria wiring need no per-call-site duplication.
 */
export const useFieldControl = () => React.useContext(FieldControlContext);

/**
 * The message id, for a control that CANNOT consume the context above — a
 * third-party engine we do not own (the MUI `TextField` inside an
 * `Autocomplete`), which reads no React context of ours and whose own
 * `getInputProps()` carries no `aria-describedby`.
 *
 * WHY THIS LIVES IN THE PRIMITIVE, not in each shell: every shell that wraps a
 * foreign engine has to hand the message id over by hand, and the first two
 * forgot — the helper/error text stayed visually correct while being orphaned
 * for a screen reader, on four call sites. A third engine shell would
 * rediscover the same five lines and nothing would fail when it did not.
 * Exporting it here makes the association a property of `Field`, so the next
 * shell imports it instead of reinventing it.
 */
export function useFieldDescribedBy(): string | undefined {
  return useFieldControl().describedBy;
}

/**
 * Merge that id into a foreign engine's `inputProps`, keeping the engine's own
 * attributes: an engine that already sets `aria-describedby` (or any other
 * input attribute) keeps it when the field has no message, instead of losing
 * the key to an overwrite.
 */
export function withFieldDescribedBy<T extends object>(
  inputProps: T,
  describedBy: string | undefined
): T {
  return describedBy
    ? { ...inputProps, 'aria-describedby': describedBy }
    : inputProps;
}

export interface FieldLabelProps
  extends React.LabelHTMLAttributes<HTMLLabelElement> {
  /** Renders `<label htmlFor>` when given; a `<span id>` otherwise. */
  htmlFor?: string;
  required?: boolean;
}

/**
 * The styled label: a real block in normal flow (14px/600, text.secondary).
 * `MuiInputLabel-root`/`MuiFormLabel-root` are INERT hooks for call-site
 * selectors (see the module docblock); no MUI CSS applies to them inside
 * `minerva-field` because `src/tailwind.css` scopes the legacy compensations
 * away from it.
 */
export function FieldLabel({
  children,
  className,
  htmlFor,
  required,
  ...rest
}: FieldLabelProps) {
  const labelClassName = cn(
    'MuiInputLabel-root MuiFormLabel-root min-w-0 text-sm font-semibold leading-[1.4] text-muted-foreground',
    // Field-level disabled state: the Field root sets `data-disabled`, so the
    // label (and the message below) can react with the design's disabled ink.
    'group-data-[disabled=true]:text-[var(--minerva-palette-text-disabled)]',
    className
  );

  const asterisk = required ? (
    <span
      aria-hidden="true"
      className="MuiFormLabel-asterisk ml-0.5 text-[color:var(--color-destructive-text)]"
    >
      *
    </span>
  ) : null;

  if (htmlFor) {
    return (
      <label
        className={labelClassName}
        data-slot="field-label"
        htmlFor={htmlFor}
        {...rest}
      >
        {children}
        {asterisk}
      </label>
    );
  }

  return (
    <span className={labelClassName} data-slot="field-label" {...rest}>
      {children}
      {asterisk}
    </span>
  );
}

export interface FieldMessageProps
  extends React.HTMLAttributes<HTMLParagraphElement> {
  invalid?: boolean;
}

/**
 * The field's single message slot: helper text by default, error text when
 * invalid (never both — that is the CTO's "one slot" rule; DESIGN-SPEC §2.2).
 */
export function FieldMessage({
  className,
  invalid,
  ...rest
}: FieldMessageProps) {
  return (
    <p
      className={cn(
        'MuiFormHelperText-root text-muted-foreground text-sm leading-[1.4]',
        invalid && 'Mui-error text-[color:var(--color-destructive-text)]',
        'group-data-[disabled=true]:text-[var(--minerva-palette-text-disabled)]',
        className
      )}
      data-slot="field-message"
      {...rest}
    />
  );
}

function FieldHelpTooltip({
  help,
  label,
}: {
  help: FieldHelpContent;
  label: string;
}) {
  return (
    // Self-contained provider: Radix throws when a Tooltip mounts outside one,
    // and the shipped precedent (custom-lists cards) had to add providers per
    // tree because of it — a field must never depend on the caller doing that.
    <TooltipProvider delayDuration={100} skipDelayDuration={0}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            aria-label={`${label} help`}
            className={cn(
              'mt-px inline-flex size-[18px] shrink-0 items-center justify-center text-muted-foreground',
              'transition-colors duration-150 ease-out hover:text-foreground',
              'focus-visible:outline-offset-2 focus-visible:[outline:2px_solid_var(--color-ring)]'
            )}
            type="button"
          >
            <BadgeInfo
              absoluteStrokeWidth
              aria-hidden="true"
              className="size-4"
              strokeWidth={2.25}
            />
          </button>
        </TooltipTrigger>
        <TooltipContent className="max-w-[360px]">
          <div className="grid gap-1.5 font-normal text-[0.8125rem] leading-[1.5]">
            <p>{help.controls}</p>
            {help.increaseEffect ? (
              <p>If you raise it: {help.increaseEffect}</p>
            ) : null}
            {help.decreaseEffect ? (
              <p>If you lower it: {help.decreaseEffect}</p>
            ) : null}
            {help.supportedValues ? (
              <p>Supported values: {help.supportedValues}</p>
            ) : null}
            {help.options ? <p>Options: {help.options}</p> : null}
            {help.note ? <p>{help.note}</p> : null}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export interface FieldProps extends React.HTMLAttributes<HTMLDivElement> {
  label?: React.ReactNode;
  /** Group controls: render the label as a span and pass this id to the
   * group's `aria-labelledby` (parity: config `FormField`). */
  labelId?: string;
  /** The control's id. When omitted the Field mints one and wires it via
   * context; controls inside default their `id` to it. A control that
   * overrides its own `id` must be paired with `htmlFor` here, or the label
   * would point at the minted id instead. */
  htmlFor?: string;
  required?: boolean;
  /** Truthy marks the field invalid (aria + border + message styling). A
   * non-boolean node replaces `helperText` as the message content. */
  error?: React.ReactNode;
  helperText?: React.ReactNode;
  help?: FieldHelpContent;
  helpLabel?: string;
  disabled?: boolean;
  labelClassName?: string;
  labelStyle?: React.CSSProperties;
  messageClassName?: string;
  messageStyle?: React.CSSProperties;
  'data-testid'?: string;
}

export const Field = React.forwardRef<HTMLDivElement, FieldProps>(
  function Field(
    {
      children,
      className,
      disabled,
      error,
      help,
      helpLabel,
      helperText,
      htmlFor,
      label,
      labelClassName,
      labelId,
      labelStyle,
      messageClassName,
      messageStyle,
      required,
      ...rest
    },
    ref
  ) {
    const autoId = React.useId();
    const controlId = htmlFor ?? autoId;
    const messageId = `${controlId}-message`;
    const invalid = Boolean(error);
    const message = invalid && typeof error !== 'boolean' ? error : helperText;
    // An empty string is "no message" too: forms carry `error ? msg : ''` from
    // the MUI era, where an empty helper cost nothing. Here a rendered <p>
    // would add a phantom 8px gap under the control.
    const showMessage =
      message !== undefined &&
      message !== null &&
      message !== false &&
      message !== '';

    const resolvedHelpLabel =
      helpLabel ?? (typeof label === 'string' ? label : 'Field');

    return (
      <div
        ref={ref}
        className={cn(
          'minerva-field group flex flex-col items-stretch gap-2',
          className
        )}
        data-disabled={disabled ? 'true' : undefined}
        {...rest}
      >
        {label !== undefined && label !== null ? (
          <div className="flex items-start gap-1.5">
            <FieldLabel
              className={labelClassName}
              // A group label (labelId) is a span the caller points
              // aria-labelledby at; otherwise the label names the control.
              htmlFor={labelId ? undefined : controlId}
              id={labelId}
              required={required}
              style={labelStyle}
            >
              {label}
            </FieldLabel>
            {help ? (
              <FieldHelpTooltip help={help} label={resolvedHelpLabel} />
            ) : null}
          </div>
        ) : null}
        <FieldControlContext.Provider
          value={{
            describedBy: showMessage ? messageId : undefined,
            disabled,
            id: controlId,
            invalid,
            required,
          }}
        >
          {children}
        </FieldControlContext.Provider>
        {showMessage ? (
          <FieldMessage
            className={messageClassName}
            id={messageId}
            invalid={invalid}
            style={messageStyle}
          >
            {message}
          </FieldMessage>
        ) : null}
      </div>
    );
  }
);
Field.displayName = 'Field';
